/**
 * Subscriptions Page - Subscription Management
 * 
 * Displays all subscriptions with comprehensive management options.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard, Search, RefreshCw, MoreVertical, Play, Pause,
  Calendar, Clock, CheckCircle, XCircle, AlertTriangle, Edit,
  ArrowUp, ArrowDown, Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { api } from '../../App';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const statusConfig = {
  ACTIVE: { label: 'Actif', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  TRIAL: { label: 'Essai', color: 'bg-blue-100 text-blue-800', icon: Clock },
  EXPIRED: { label: 'Expiré', color: 'bg-red-100 text-red-800', icon: XCircle },
  SUSPENDED: { label: 'Suspendu', color: 'bg-orange-100 text-orange-800', icon: Pause },
  CANCELLED: { label: 'Annulé', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  LIFETIME: { label: 'Lifetime', color: 'bg-purple-100 text-purple-800', icon: CheckCircle }
};

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Dialogs
  const [extendDialog, setExtendDialog] = useState({ open: false, subscription: null });
  const [changePlanDialog, setChangePlanDialog] = useState({ open: false, subscription: null });
  const [suspendDialog, setSuspendDialog] = useState({ open: false, subscription: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, subscription: null });
  
  // Form state
  const [extendMonths, setExtendMonths] = useState(1);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    fetchSubscriptions();
    fetchPlans();
  }, [statusFilter, pagination.page]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', 20);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const { data } = await api.get(`/admin/subscriptions?${params}`);
      
      // Filter by search client-side
      let filtered = data.subscriptions;
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(s => 
          s.riad?.name?.toLowerCase().includes(searchLower) ||
          s.account?.name?.toLowerCase().includes(searchLower) ||
          s.plan?.toLowerCase().includes(searchLower)
        );
      }
      
      setSubscriptions(filtered);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/admin/plans?status=ACTIVE');
      setPlans(data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubscriptions();
  };

  const handleExtend = async () => {
    try {
      await api.post(`/admin/subscriptions/${extendDialog.subscription.id}/extend`, {
        months: extendMonths
      });
      toast.success('Abonnement prolongé');
      setExtendDialog({ open: false, subscription: null });
      setExtendMonths(1);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleChangePlan = async () => {
    if (!selectedPlanId) return;
    
    try {
      await api.post(`/admin/subscriptions/${changePlanDialog.subscription.id}/change-plan`, {
        plan_id: selectedPlanId,
        adjust_price: true
      });
      toast.success('Plan changé avec succès');
      setChangePlanDialog({ open: false, subscription: null });
      setSelectedPlanId(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleSuspend = async () => {
    try {
      await api.put(`/admin/subscriptions/${suspendDialog.subscription.id}`, {
        status: 'SUSPENDED'
      });
      toast.success('Abonnement suspendu');
      setSuspendDialog({ open: false, subscription: null });
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleActivate = async (subscription) => {
    try {
      await api.put(`/admin/subscriptions/${subscription.id}`, {
        status: 'ACTIVE'
      });
      toast.success('Abonnement activé');
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleCancel = async () => {
    try {
      await api.put(`/admin/subscriptions/${cancelDialog.subscription.id}`, {
        status: 'CANCELLED',
        cancelled_at: new Date().toISOString()
      });
      toast.success('Abonnement annulé');
      setCancelDialog({ open: false, subscription: null });
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  // Calculate stats
  const stats = {
    active: subscriptions.filter(s => s.status === 'ACTIVE').length,
    trial: subscriptions.filter(s => s.status === 'TRIAL').length,
    expiring: subscriptions.filter(s => {
      if (!s.end_date) return false;
      const days = differenceInDays(new Date(s.end_date), new Date());
      return days >= 0 && days <= 30;
    }).length,
    totalRevenue: subscriptions
      .filter(s => ['ACTIVE', 'TRIAL'].includes(s.status))
      .reduce((sum, s) => sum + parseFloat(s.monthly_price || 0), 0)
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Abonnements</h1>
          <p className="text-muted-foreground">Gestion des abonnements SaaS</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.active}</p>
              <p className="text-sm text-muted-foreground">Actifs</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.trial}</p>
              <p className="text-sm text-muted-foreground">En essai</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.expiring}</p>
              <p className="text-sm text-muted-foreground">Expirent bientôt</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.totalRevenue.toLocaleString()} MAD</p>
              <p className="text-sm text-muted-foreground">Revenus mensuels</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par client, riad..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
              data-testid="subscription-search-input"
            />
          </div>
        </form>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="ACTIVE">Actif</SelectItem>
            <SelectItem value="TRIAL">Essai</SelectItem>
            <SelectItem value="EXPIRED">Expiré</SelectItem>
            <SelectItem value="SUSPENDED">Suspendu</SelectItem>
            <SelectItem value="CANCELLED">Annulé</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchSubscriptions}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Subscriptions Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client / Riad</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Prix</TableHead>
                <TableHead>Début</TableHead>
                <TableHead>Expiration</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    Aucun abonnement trouvé
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map(sub => {
                  const StatusIcon = statusConfig[sub.status]?.icon || CheckCircle;
                  const daysRemaining = sub.end_date 
                    ? differenceInDays(new Date(sub.end_date), new Date())
                    : null;
                  
                  return (
                    <TableRow key={sub.id} data-testid={`subscription-row-${sub.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-violet-600" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {sub.account?.name || sub.riad?.name || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {sub.riad?.name && sub.account?.name ? sub.riad.name : sub.riad?.slug || '-'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {sub.planDetails ? (
                          <Badge 
                            variant="outline"
                            style={{ 
                              borderColor: sub.planDetails.color,
                              color: sub.planDetails.color
                            }}
                          >
                            {sub.planDetails.name}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">{sub.plan}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig[sub.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[sub.status]?.label || sub.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {sub.monthly_price} {sub.currency}
                        </span>
                        <span className="text-xs text-muted-foreground">/mois</span>
                      </TableCell>
                      <TableCell>
                        {sub.start_date 
                          ? format(new Date(sub.start_date), 'dd/MM/yyyy')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {sub.end_date ? (
                          <div>
                            <p className={
                              daysRemaining < 0 ? 'text-red-600 font-medium' :
                              daysRemaining < 30 ? 'text-orange-600 font-medium' : ''
                            }>
                              {format(new Date(sub.end_date), 'dd/MM/yyyy')}
                            </p>
                            {daysRemaining !== null && (
                              <p className="text-xs text-muted-foreground">
                                {daysRemaining < 0 
                                  ? `Expiré il y a ${Math.abs(daysRemaining)}j`
                                  : `Dans ${daysRemaining}j`
                                }
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Illimité</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {sub.status === 'ACTIVE' && (
                              <DropdownMenuItem onClick={() => setSuspendDialog({ open: true, subscription: sub })}>
                                <Pause className="w-4 h-4 mr-2" />
                                Suspendre
                              </DropdownMenuItem>
                            )}
                            {['SUSPENDED', 'EXPIRED'].includes(sub.status) && (
                              <DropdownMenuItem onClick={() => handleActivate(sub)}>
                                <Play className="w-4 h-4 mr-2" />
                                Activer
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => setExtendDialog({ open: true, subscription: sub })}>
                              <Calendar className="w-4 h-4 mr-2" />
                              Prolonger
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setChangePlanDialog({ open: true, subscription: sub });
                              setSelectedPlanId(sub.plan_id);
                            }}>
                              <ArrowUp className="w-4 h-4 mr-2" />
                              Changer de plan
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => setCancelDialog({ open: true, subscription: sub })}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Annuler
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            Précédent
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {pagination.page} / {pagination.pages}
          </span>
          <Button
            variant="outline"
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            Suivant
          </Button>
        </div>
      )}

      {/* Extend Dialog */}
      <Dialog open={extendDialog.open} onOpenChange={(open) => setExtendDialog({ open, subscription: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prolonger l'abonnement</DialogTitle>
            <DialogDescription>
              Prolonger l'abonnement de <strong>{extendDialog.subscription?.riad?.name || extendDialog.subscription?.account?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Nombre de mois</Label>
              <Select value={String(extendMonths)} onValueChange={v => setExtendMonths(parseInt(v))}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 mois</SelectItem>
                  <SelectItem value="3">3 mois</SelectItem>
                  <SelectItem value="6">6 mois</SelectItem>
                  <SelectItem value="12">12 mois</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {extendDialog.subscription?.end_date && (
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-sm text-muted-foreground">Nouvelle date d'expiration:</p>
                <p className="font-medium">
                  {format(
                    new Date(new Date(extendDialog.subscription.end_date).setMonth(
                      new Date(extendDialog.subscription.end_date).getMonth() + extendMonths
                    )),
                    'dd MMMM yyyy',
                    { locale: fr }
                  )}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialog({ open: false, subscription: null })}>
              Annuler
            </Button>
            <Button onClick={handleExtend}>Prolonger</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={changePlanDialog.open} onOpenChange={(open) => setChangePlanDialog({ open, subscription: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Changer de plan</DialogTitle>
            <DialogDescription>
              Sélectionnez le nouveau plan pour cet abonnement
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {plans.map(plan => (
              <div
                key={plan.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedPlanId === plan.id
                    ? 'border-violet-600 bg-violet-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPlanId(plan.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold" style={{ color: plan.color }}>
                    {plan.name}
                  </span>
                  {plan.badge && (
                    <Badge variant="secondary">{plan.badge}</Badge>
                  )}
                </div>
                <p className="text-2xl font-bold">
                  {plan.price} {plan.currency}
                  <span className="text-sm font-normal text-muted-foreground">/{plan.periodicity}</span>
                </p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePlanDialog({ open: false, subscription: null })}>
              Annuler
            </Button>
            <Button onClick={handleChangePlan} disabled={!selectedPlanId}>
              Changer le plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <AlertDialog open={suspendDialog.open} onOpenChange={(open) => setSuspendDialog({ open, subscription: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspendre l'abonnement</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir suspendre cet abonnement ? Le tenant ne pourra plus accéder à la plateforme.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleSuspend} className="bg-orange-600 hover:bg-orange-700">
              Suspendre
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ open, subscription: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler l'abonnement</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir annuler définitivement cet abonnement ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-red-600 hover:bg-red-700">
              Annuler l'abonnement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubscriptionsPage;
